import { t } from "../i18n";
import { useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { errorMessage, isApiError } from "../api/client";
import { Button } from "../ui/Button";
import { TextField } from "../ui/TextField";
import { AuthLayout, PasswordField } from "./AuthLayout";
import { useAuth } from "./useAuth";

const GOOGLE_G = (
  <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden>
    <path fill="#4285F4" d="M45 24c0-1.6-.1-2.7-.4-4H24v7.5h12c-.2 2-1.5 5-4.4 7l6.7 5.2C42.2 36 45 30.6 45 24z" />
    <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.3l-6.7-5.2c-1.8 1.3-4.3 2.2-7.8 2.2-6 0-11-4-12.8-9.4l-7 5.4C7.8 41 15.3 46 24 46z" />
    <path fill="#FBBC05" d="M11.2 28.3c-.5-1.4-.8-2.8-.8-4.3s.3-3 .7-4.3l-7-5.4C2.8 17.2 2 20.5 2 24s.8 6.8 2.2 9.7l7-5.4z" />
    <path fill="#EA4335" d="M24 10.4c4.2 0 7.1 1.8 8.7 3.3l6-5.8C34.9 4.5 29.9 2 24 2 15.3 2 7.8 7 4.2 14.3l7 5.4C13 14.4 18 10.4 24 10.4z" />
  </svg>
);

function GoogleButton(): JSX.Element {
  const url = useAuth((s) => s.googleUrl);
  return <a href={url} className="btn-ghost justify-center border border-rule">{t("Google ile devam et")}</a>;
}

const Spinner = (): JSX.Element => <span className="lp-spin" aria-hidden />;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** /giris — iki sütunlu giriş sayfası (design_handoff_giris_sayfasi). Doğrulama gönderimde; alan hataları touched + değerlerden türetilir. */
export function LoginPage(): JSX.Element {
  const nav = useNavigate();
  const [q] = useSearchParams();
  const login = useAuth((s) => s.login);
  const googleUrl = useAuth((s) => s.googleUrl);
  const [email, setEmail] = useState(""); const [pw, setPw] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState<null | "form" | "google">(null);
  const [touched, setTouched] = useState(false);
  const [formError, setFormError] = useState<{ text: string; unverified?: boolean }>();
  const notice = q.get("sifirlandi") ? t("Parolanız değiştirildi. Yeni parolanızla giriş yapın.") : undefined;
  const emailRef = useRef<HTMLInputElement | null>(null); const pwRef = useRef<HTMLInputElement | null>(null);

  const emailErr = !touched ? undefined : !email.trim() ? t("E-posta adresinizi girin.") : !EMAIL_RE.test(email.trim()) ? t("Geçerli bir e-posta adresi girin.") : undefined;
  const pwErr = touched && !pw ? t("Parolanızı girin.") : undefined;
  const apiInvalid = !!formError && !formError.unverified;
  const edit = (set: (v: string) => void) => (v: string): void => { set(v); setFormError(undefined); };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault(); setTouched(true); setFormError(undefined);
    const bad = !email.trim() || !EMAIL_RE.test(email.trim()) ? emailRef.current : !pw ? pwRef.current : null;
    if (bad) { bad.focus(); return; }
    setBusy("form");
    try { await login(email.trim(), pw, remember); nav("/"); }
    catch (ex) {
      setFormError(isApiError(ex, "EMAIL_NOT_VERIFIED") ? { text: t("E-posta adresiniz henüz doğrulanmamış. Gelen kutunuzdaki bağlantıya tıklayın ya da doğrulama e-postasını yeniden gönderin."), unverified: true }
        : isApiError(ex, "INVALID_CREDENTIALS") ? { text: t("E-posta veya parola hatalı.") } : { text: errorMessage(ex) });
      setBusy(null);
    }
  };

  return (
    <AuthLayout variant="landing" title={t("Giriş yap")}>
      <section aria-label={t("Giriş yap")} className="lp-card w-full max-w-[420px] justify-self-center bg-sheet border border-rule rounded-lg shadow-[0_12px_30px_-16px_rgb(22_35_59/0.28)]">
        <form noValidate className="grid gap-[16px] px-[26px] pt-[26px] pb-6" onSubmit={(e) => void submit(e)}>
          <h2 className="text-[20px] font-semibold tracking-[-0.02em]">{t("Giriş yap")}</h2>
          {formError ? (
            <p role="alert" className="lp-alert">{formError.text}{formError.unverified ? <> <Link to={`/dogrula?email=${encodeURIComponent(email.trim())}`} className="underline font-medium">{t("Yeniden gönder")}</Link></> : null}</p>
          ) : notice ? <p role="status" className="lp-notice">{notice}</p> : null}
          <a href={googleUrl} aria-disabled={busy ? true : undefined} onClick={(e) => { if (busy) e.preventDefault(); else setBusy("google"); }} className="lp-google">
            {busy === "google" ? <Spinner /> : GOOGLE_G}{t("Google ile devam et")}
          </a>
          <div className="flex items-center gap-[12px] text-[12px] text-[#5A6068]"><span className="h-px flex-1 bg-[#E9E7E1]" />{t("veya e-posta ile")}<span className="h-px flex-1 bg-[#E9E7E1]" /></div>
          <div className="pw-field flex flex-col gap-1">
            <label htmlFor="li-email">{t("E-posta")}</label>
            <input id="li-email" ref={emailRef} type="email" autoComplete="email" className="lp-input" placeholder="ad@firma.com.tr" value={email}
              aria-invalid={emailErr || apiInvalid ? "true" : undefined} aria-describedby={emailErr ? "li-email-err" : undefined} onChange={(e) => edit(setEmail)(e.target.value)} />
            {emailErr ? <p id="li-email-err" className="field-err">{emailErr}</p> : null}
          </div>
          <PasswordField id="li-pw" label={t("Parola")} value={pw} onChange={edit(setPw)} autoComplete="current-password" error={pwErr} invalid={apiInvalid}
            inputRef={(el) => { pwRef.current = el; }} aside={<Link to="/sifre" className="text-[13px] text-water hover:underline">{t("Parolamı unuttum")}</Link>} />
          <label className="flex items-center gap-[8px] text-[13.5px] text-[#3A3F45] cursor-pointer w-fit">
            <input type="checkbox" className="w-[15px] h-[15px] accent-[#16233B]" checked={remember} onChange={(e) => setRemember(e.target.checked)} />{t("Beni hatırla")}
          </label>
          <button type="submit" className="lp-submit" disabled={busy !== null}>{busy === "form" ? <><Spinner />{t("Giriş yapılıyor…")}</> : t("Giriş")}</button>
        </form>
        <p className="border-t border-[#E9E7E1] bg-[#FBFAF7] rounded-b-lg px-[26px] py-[14px] text-[13.5px] text-[#3A3F45]">
          {t("Hesabınız yok mu?")} <Link to="/kayit" aria-label={t("Hesap oluştur")} className="font-semibold text-water hover:underline">{t("Ücretsiz hesap açın")}</Link>{t(" — kart istenmez.")}
        </p>
      </section>
    </AuthLayout>
  );
}

export function RegisterPage(): JSX.Element {
  const nav = useNavigate();
  const register = useAuth((s) => s.register);
  const [f, setF] = useState({ name: "", email: "", password: "", chamberNo: "", org: "" });
  const [err, setErr] = useState<string>(); const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF({ ...f, [k]: v });
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault(); setBusy(true); setErr(undefined);
    try {
      await register({ name: f.name, email: f.email, password: f.password, chamberNo: f.chamberNo || undefined, org: f.org || undefined });
      nav(`/dogrula?email=${encodeURIComponent(f.email)}&yeni=1`);
    } catch (ex) { setErr(isApiError(ex, "EMAIL_TAKEN") ? "Bu e-posta ile bir hesap zaten var." : errorMessage(ex)); }
    finally { setBusy(false); }
  };
  return (
    <AuthLayout title={t("Hesap oluştur")} footer={<><span>{t("Zaten hesabınız var mı?")}</span><Link to="/giris" className="underline">{t("Giriş yap")}</Link></>}>
      <form className="grid gap-3" onSubmit={(e) => void submit(e)}>
        <TextField id="rg-name" label={t("Ad Soyad")} value={f.name} onChange={set("name")} required />
        <TextField id="rg-email" label={t("E-posta")} value={f.email} onChange={set("email")} required />
        <PasswordField id="rg-pw" label={t("Parola (en az 8 karakter)")} value={f.password} onChange={set("password")} autoComplete="new-password" />
        <div className="grid grid-cols-2 gap-2">
          <TextField id="rg-chamber" label={t("Oda sicil no")} value={f.chamberNo} onChange={set("chamberNo")} />
          <TextField id="rg-org" label={t("Firma")} value={f.org} onChange={set("org")} />
        </div>
        <p className="text-[11px] text-stone">{t("Kayıt olarak")} <Link to="/kullanim-sartlari" className="underline">{t("kullanım şartlarını")}</Link> {t("ve")} <Link to="/gizlilik" className="underline">{t("gizlilik bildirimini")}</Link> {t("kabul edersiniz. Ücretsiz hesapta kontrol sonuçlarını görürsünüz; rapor ve çıktılar aboneliğe dahildir.")}</p>
        {err ? <p role="alert" className="text-xs text-bad">{err}</p> : null}
        <Button type="submit" disabled={busy || !f.name || !f.email || f.password.length < 8}>{t("Kayıt ol")}</Button>
        <GoogleButton />
      </form>
    </AuthLayout>
  );
}

/** Kayıt sonrası bilgi ekranı; doğrulama e-postasını yeniden gönderir. */
export function VerifyPage(): JSX.Element {
  const [q] = useSearchParams();
  const email = q.get("email") ?? "";
  const [msg, setMsg] = useState<string>();
  const resend = async (): Promise<void> => {
    try { await import("../api/client").then(({ api }) => api("POST", "/auth/resend", { email })); setMsg("Doğrulama e-postası yeniden gönderildi."); }
    catch (ex) { setMsg(errorMessage(ex)); }
  };
  return (
    <AuthLayout title={q.get("yeni") ? t("E-postanızı doğrulayın") : t("Doğrulama e-postası")} footer={<Link to="/giris" className="underline">{t("Giriş sayfası")}</Link>}>
      <p className="text-sm text-ink/75"><strong>{email}</strong> {t("adresine bir doğrulama bağlantısı gönderdik. Bağlantıya tıkladığınızda hesabınız açılır ve oturum başlar.")}</p>
      <p className="text-xs text-stone">{t("E-posta gelmediyse gereksiz klasörüne bakın.")}</p>
      {msg ? <p role="status" className="text-xs text-ink/75">{msg}</p> : null}
      <Button variant="ghost" disabled={!email} onClick={() => void resend()}>{t("Yeniden gönder")}</Button>
    </AuthLayout>
  );
}

export function ForgotPage(): JSX.Element {
  const forgot = useAuth((s) => s.forgot);
  const [email, setEmail] = useState(""); const [done, setDone] = useState(false); const [err, setErr] = useState<string>();
  return (
    <AuthLayout title={t("Parolamı unuttum")} footer={<Link to="/giris" className="underline">{t("Giriş sayfası")}</Link>}>
      {done ? <p role="status" className="text-sm text-ink/75">{t("Bu adresle kayıtlı bir hesap varsa sıfırlama bağlantısı gönderildi (1 saat geçerli).")}</p> : (
        <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); forgot(email).then(() => setDone(true)).catch((ex: unknown) => setErr(errorMessage(ex))); }}>
          <TextField id="fg-email" label={t("E-posta")} value={email} onChange={setEmail} />
          {err ? <p role="alert" className="text-xs text-bad">{err}</p> : null}
          <Button type="submit" disabled={!email}>{t("Bağlantı gönder")}</Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPage(): JSX.Element {
  const [q] = useSearchParams();
  const nav = useNavigate();
  const reset = useAuth((s) => s.reset);
  const token = q.get("token") ?? "";
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState(""); const [err, setErr] = useState<string>();
  return (
    <AuthLayout title={t("Yeni parola")}>
      {!token ? <p role="alert" className="text-sm text-bad">{t("Bağlantı eksik. E-postadaki bağlantıyı kullanın.")}</p> : (
        <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); if (pw !== pw2) { setErr("Parolalar aynı değil"); return; } reset(token, pw).then(() => nav("/giris?sifirlandi=1")).catch((ex: unknown) => setErr(isApiError(ex, "TOKEN_INVALID") ? "Bağlantı geçersiz ya da süresi dolmuş." : errorMessage(ex))); }}>
          <PasswordField id="rs-pw" label={t("Yeni parola (en az 8 karakter)")} value={pw} onChange={setPw} autoComplete="new-password" />
          <PasswordField id="rs-pw2" label={t("Yeni parola (tekrar)")} value={pw2} onChange={setPw2} autoComplete="new-password" />
          {err ? <p role="alert" className="text-xs text-bad">{err}</p> : null}
          <Button type="submit" disabled={pw.length < 8}>{t("Parolayı değiştir")}</Button>
        </form>
      )}
    </AuthLayout>
  );
}
